import React, { useContext, useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import DataContext from "./Controller";
import PopUpModal from "./PopUpModal";
import { countryList } from "../utils/countyCode";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";

const Form = () => {
  const {
    formData,
    handleChange,
    handleSubmit,
    otherFormPart,
    handleFormStep,
    modalVisibility,
    loading,
    handleCountryChange,
  } = useContext(DataContext);

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(countryList[21]);

  // Memoize filtered countries to prevent unnecessary recalculations
  const filteredCountries = useMemo(() => {
    return query.length > 0 && query
      ? countryList.filter(({ name }) =>
          name.toLowerCase().includes(query.toLowerCase())
        )
      : countryList;
  }, [query, countryList]);

  useEffect(() => {
    handleCountryChange(selected);
  }, [selected]);

  return (
    <div className="w-full flex flex-col items-center justify-center mt-64 md:mt-0">
      {modalVisibility && (
        <div className="absolute w-full h-full bg-black bg-opacity-50 z-50">
          <PopUpModal />
        </div>
      )}

      <form
        className="md:w-[50%] w-full flex flex-col items-center justify-between py-4 gap-3 mt-5 shadow-xl px-3 rounded-lg"
        onSubmit={handleSubmit}
      >
        <AnimatePresence mode="wait">
          {!otherFormPart && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="w-full flex flex-col items-start justify-center gap-3"
            >
              <h2 className="md:text-xl text-lg capitalize text-center w-full font-bold">
                You’re about to make your recipient smile 😊 with a Wordygift!
              </h2>
              <p className="w-full text-center md:text-md text-base font-semibold capitalize">
                Enter your name and email as the sender.
              </p>
              <input
                type="text"
                name="payeeName"
                placeholder="Your Name"
                value={formData.payeeName}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg shadow-lg"
                required
              />

              <input
                type="email"
                name="payeeEmail"
                placeholder="Your Email"
                value={formData.payeeEmail}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg shadow-lg"
                required
              />

              <button
                type="button"
                className="w-[150px] bg-[#C8266F] text-white font-bold text-lg focus:scale-90 shadow-[inset_0px_4px_6px_rgba(0,0,0,0.2)] py-2 rounded-lg outline-none"
                onClick={handleFormStep}
              >
                Continue
              </button>
            </motion.div>
          )}

          {otherFormPart && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="w-full flex flex-col items-start justify-center gap-4"
            >
              <h2 className="md:text-xl text-lg capitalize text-left w-full font-bold">
                Almost there! 🎉
              </h2>

              {/* Recipient's Name */}
              <label className="font-medium text-gray-700">
                Recipient's First Name (or Nickname)
              </label>
              <input
                type="text"
                name="recipient_name"
                placeholder="Enter first name or nickname"
                value={formData.recipient_name}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg shadow-lg mb-3"
                required
              />

              <div className="w-full lg:flex lg:gap-3">
                <div className="lg:w-[42%]">
                  <label className="font-medium text-gray-700">
                    Select a Country Code
                  </label>
                  <Combobox
                    value={selected}
                    onChange={setSelected}
                    onClose={() => setQuery("")}
                  >
                    <div className="relative">
                      <ComboboxInput
                        className="w-full p-2 border rounded-lg shadow-lg mb-3"
                        displayValue={(country) =>
                          country
                            ? `${country.phoneCode} ${country.name} (${country.timeZone})`
                            : ""
                        }
                        onChange={(e) => setQuery(e.target.value)}
                      />
                      <ComboboxButton className="absolute inset-y-0 -right-1 bottom-3 px-2.5 group">
                        <ChevronDownIcon className="size-6 fill-black" />
                      </ComboboxButton>
                    </div>

                    {filteredCountries.length > 0 && (
                      <ComboboxOptions
                        anchor="bottom"
                        transition
                        className={clsx(
                          "w-[var(--input-width)] rounded-xl border bg-white p-1 transition duration-100 ease-in",
                          "[--anchor-gap:var(--spacing-1)] empty:invisible data-[leave]:data-[closed]:opacity-0"
                        )}
                      >
                        {filteredCountries.map((country) => (
                          <ComboboxOption
                            key={`${country.phoneCode}-${country.name}`}
                            value={country}
                            className="group flex cursor-default items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-white/10"
                          >
                            <CheckIcon className="size-4 fill-black invisible group-data-[selected]:visible" />
                            <div className="text-sm/6">
                              {country.phoneCode} <b>{country.name}</b> (
                              {country.timeZone})
                            </div>
                          </ComboboxOption>
                        ))}
                      </ComboboxOptions>
                    )}
                  </Combobox>
                </div>
                <div className="lg:w-[58%]">
                  {/* Phone Number */}
                  <label className="font-medium text-gray-700">
                    Recipient's Phone Number (for SMS)
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Enter phone number"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg shadow-lg mb-3"
                    required
                  />
                </div>
              </div>
              {/* Country Code */}

              {/* Date of Birth */}
              <label className="font-medium text-gray-700">
                Recipient’s Date of Birth
              </label>
              <input
                type="date"
                name="birthdate"
                value={formData.birthdate}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg shadow-lg mb-3"
              />

              {/* Tone Selection */}
              <label className="font-medium text-gray-700">
                Select Tone of the Wordygift
              </label>
              <select
                name="tone"
                value={formData.tone}
                onChange={handleChange}
                className="w-full p-2 border rounded-lg shadow-lg mb-3"
              >
                <option disabled>Select tone</option>
                <option value="Motivational">Motivational</option>
                <option value="Inspiring">Inspiring</option>
                <option value="Uplifting">Uplifting</option>
              </select>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-[#C8266F] text-white font-bold text-lg focus:scale-95 transition-transform py-2 rounded-lg shadow-lg flex items-center justify-center"
              >
                {!loading ? (
                  "Submit Request"
                ) : (
                  <div role="status">
                    <svg
                      aria-hidden="true"
                      className="w-6 h-6 text-gray-200 animate-spin fill-[#C8266F]"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                    <span className="sr-only">Loading...</span>
                  </div>
                )}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </form>
    </div>
  );
};

export default Form;
